import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Link from '@mui/material/Link';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { Accordion } from 'components/molecules/Accordion';
import { useTranslations } from 'hooks/useTranslations';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { ERouteLinks } from 'models/route';
import { useAlert } from 'models/alertContext';
import { Alert, EAlertVariants, IAlert } from 'components/atoms/Alert';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { Chips } from 'components/atoms/Chips';
import { getFieldsByComponentType } from 'pages/AncillariesPage/helpers';
import {
  EAncillaryState,
  EArtAncillaryTypeName,
  EArtAncillaryTypeNameLowerCase,
  EArtComponentType,
} from 'constants/ArtAccessoryType';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { IAncillaryDetailsCustomfield } from 'models/ancillaries';
import { ECustomFieldObjectType, ECustomFieldType } from 'models/customField';
import { clearAlertTimeout, EAlertTime, formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { ActionTypes } from 'state/actions/alert';
import { EFirearmStateId } from 'models/firearm';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { Asset } from 'utils/assets';
import { EState, statesWithoutMovedTransaction } from 'utils/transactionUtils';
import { ComponentInformationContainer, ComponentInformationTitle } from './styles';
import { ComponentsTransactions } from './ComponentsTransactions';
import { ComponentsComments } from './ComponentsComments';
import { ComponentsAttachments } from './ComponentsAttachments';
import ImagesGallery from 'components/organisms/ImagesGallery';
import { EModuleType } from 'components/organisms/ImagesGallery/helper';
import ImagesGalleryRadioButton from 'components/organisms/ImagesGalleryRadioButton';
import { EGalleryTypeAncillary } from 'components/organisms/ImagesGalleryRadioButton/helpers';
import { EMarkingTypes } from 'constants/Markings';
import { findInDictionary } from 'helpers/dictionary';
import { useDictionary } from 'models/dictionaryContext';

export enum EComponentLabels {
  componentGeneralInformation = 'componentDetails.componentGeneralInformation.header',
  componentDetailedInformation = 'componentDetails.componentDetailedInformation.header',
  componentMarkings = 'componentDetails.componentMarkings.header',
  firearmInformation = 'componentDetails.firearmInformation.header',
  ancillaryType = 'ancillaryDetails.ancillaryType.label',
  componentType = 'componentDetails.componentType.label',
  artId = 'componentDetails.artId.label',
  hostType = 'componentDetails.hostType.label',
  manufacturer = 'componentDetails.componentManufacturer.label',
  productName = 'componentDetails.productName.label',
  calibre = 'componentDetails.calibre.label',
  composition = 'componentDetails.composition.label',
  proofHouse = 'componentDetails.proofhouse.label',
  capacity = 'componentDetails.capacity.label',
  length = 'componentDetails.length.label',
  firecycleID = 'componentDetails.firecycleID.label',
  serialNumber = 'componentDetails.serialNumber.label',
  componentState = 'componentDetails.componentState.label',
  exhibitNumber = 'componentDetails.exhibitNumber.label',
  currentOwner = 'componentDetails.currentOwner.label',
  currentKeeper = 'componentDetails.currentKeeper.label',
  manufacturedDate = 'componentDetails.manufacturedDate.label',

  firearmManufacturer = 'componentDetails.firearmManufacturer.label',
  firearmReferenceNo = 'componentDetails.firearmReferenceNo.label',
  firearmFirecycleType = 'componentDetails.firecycleType.label',
  firearmMake = 'componentDetails.make.label',
  firearmModel = 'componentDetails.model.label',

  importMarking = 'componentDetails.importMarking.label',
  exportMarking = 'componentDetails.exportMarking.label',
  deactivationMarking = 'componentDetails.deactivationMarking.label',
  confiscationMarking = 'componentDetails.confiscationMarking.label',
  surplusSaleMarking = 'componentDetails.surplusSaleMarking.label',
  appliedMarking = 'componentDetails.appliedMarking.label',
  marking = 'transactionDetails.ancillaryDetails.accordion.marking.label',
  yearOfTransfer = 'transactionDetails.ancillaryDetails.accordion.yearOfTransfer.label',
  yearOfConfiscation = 'transactionDetails.ancillaryDetails.accordion.yearOfConfiscation.label',
}

const DetailComponents = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslations();
  const { selectedLanguage, defaultLanguage } = useLang();
  const navigate = useNavigate();
  const { nextPageAlert, clearAlert, transactionAlert, ancillaryAlert, setAlert } = useAlert();
  const {
    loadDictionaries,
    componentDetail,
    getComponentDetail,
    artComponentType,
    artComponentManufacturer,
    artComponentCalibre,
  } = useAncillaries();
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const [isAllExpanded, setIsAllExpanded] = useState<boolean>(false);
  const { shortDateFormat } = useGlobalProperty();
  const { hasPermissions } = usePermission();
  const { getCustomField, customFieldsAncillaries } = useLegalEntities();
  const { isAnyLicenceAuthorityValid } = usePrivilegesValidation();
  const [galleryType, setGalleryType] = useState<EGalleryTypeAncillary>(
    EGalleryTypeAncillary.ART,
  );
  const [showMarkings, setShowMarkings] = useState(false);
  const {
    getMake,
    make,
    getModel,
    model,
    getFirearmType,
    firearmType,
    getManufacturer,
    manufacturer,
  } = useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getMake();
      getModel();
      getManufacturer();
      getFirearmType();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (componentDetail) {
      if (componentDetail.typeUniqueId) {
        const fields = getFieldsByComponentType(
          componentDetail.typeUniqueId as EArtComponentType,
        );
        setAccessFields(fields);
      } else {
        setAccessFields([]);
      }
    }
  }, [componentDetail]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getComponentDetail(id);
      }
    })();
  }, [id]);

  useEffect(
    () => {
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_SAME_PAGE_ALERT, EAlertTime.SHORT);
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_NEXT_PAGE_ALERT, EAlertTime.SHORT);
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_TRANSACTION_ALERT, EAlertTime.SHORT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_ANCILLARY_ALERT);
    },
    [],
  );

  useEffect(() => {
    (async () => {
      await getCustomField(ECustomFieldObjectType.AN_COMPONENT);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([
        EDictionaryTypes.ArtAncillaryOptions,
        EDictionaryTypes.ArtComponentType,
        EDictionaryTypes.ArtComponentManufacturer,
        EDictionaryTypes.ArtComponentCalibre,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const showField = (fieldName: string) => accessFields.includes(fieldName);

  const licenceAuthorityValid =
    !!componentDetail && isAnyLicenceAuthorityValid(componentDetail.licenceAuthorityIds);

  const renderCustomFieldData = (customField: IAncillaryDetailsCustomfield) => {
    if (customField.customFieldType === ECustomFieldType.BOOL) {
      if (customField.value === null) {
        return '-';
      }
      return customField.value
        ? t('customFields.value.yes.label')
        : t('customFields.value.no.label');
    }
    if (customField.customFieldType === ECustomFieldType.DATE) {
      return formatDate(customField.value, shortDateFormat);
    }
    return customField.value || '-';
  };

  const navigateToMap = () => {
    navigate(ERouteLinks.RegisteredAncillariesMap, {
      state: {
        ancillaryUniqueId: componentDetail?.uniqueId,
        ancillaryType: EArtAncillaryTypeName.COMPONENTS.toLowerCase(),
        legalityUniqueId: componentDetail?.legalityUniqueId,
        navigateBack: `${ERouteLinks.ViewComponents.replace(':id', id as string)}`,
        showAdvanced: true,
        showLegalityBar: false,
        breadcrumbs: [
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.components.name'),
            route: `${ERouteLinks.Ancillaries}?expand=components`,
          },
          {
            label: t('componentDetails.accordion.title.label'),
            route: `${ERouteLinks.ViewComponents.replace(':id', id as string)}`,
          },
          t('ancillaries.ancillariesLocation.showOnMap.label'),
        ],
      },
    });
  };

  const handleRedirectToEditPageButton = (): (() => void) => {
    if (
      customFieldsAncillaries?.length &&
      componentDetail?.stateUniqueId !== EFirearmStateId.DISABLED
    ) {
      return () =>
        navigate(
          ERouteLinks.EditComponents.replace(':id', componentDetail?.uniqueId as string),
        );
    }
    if (componentDetail?.stateUniqueId === EFirearmStateId.DISABLED) {
      return () =>
        setAlert(ActionTypes.SET_ANCILLARY_ALERT, {
          text: t('ancillary.disabled.edit.info'),
          variant: EAlertVariants.error,
        } as IAlert);
    }
    return () =>
      setAlert(ActionTypes.SET_ANCILLARY_ALERT, {
        text: t('general.noAvaibleDataToEdit.label'),
        variant: EAlertVariants.warning,
      } as IAlert);
  };

  const handleRedirectToMovedTransaction = () => {
    if (statesWithoutMovedTransaction.includes(componentDetail?.stateUniqueId as EState))
      return;

    if (componentDetail?.groupName) {
      setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
        text: t('ancillary.transaction.addTransaction.text'),
        variant: EAlertVariants.warning,
      } as IAlert);
      clearAlert(ActionTypes.SET_SAME_PAGE_ALERT);
      return;
    }

    navigate(ERouteLinks.MoveTransaction, {
      state: {
        assetUniqueId: componentDetail?.uniqueId,
        asset: Asset.ANCILLARY,
        isGroup: false,
        searchAddressesFor: componentDetail?.keeperId || componentDetail?.ownerId,
        navigateBack: `${ERouteLinks.ViewComponents.replace(':id', id as string)}`,
        breadcrumbs: [
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.components.name'),
            route: `${ERouteLinks.Ancillaries}?expand=components`,
          },
          {
            label: t('componentDetails.accordion.title.label'),
            route: `${ERouteLinks.ViewComponents.replace(':id', id as string)}`,
          },
          t('transactions.movedTransaction.breadcrumb.label'),
        ],
      },
    });
  };

  return (
    <div>
      <Breadcrumbs
        items={[
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.components.name'),
            route: `${ERouteLinks.Ancillaries}?expand=components`,
          },
          t('componentDetails.accordion.title.label'),
        ]}
      />
      {componentDetail && (
        <Grid sx={{ gap: 3 }}>
          <DetailsTemplate
            title={findInDictionary(componentDetail?.typeUniqueId, artComponentType)}
            pageType={EDetailsPageTypes.view}
            redirectToShowOnMap={
              ![
                EAncillaryState.DESTROYED,
                EAncillaryState.DEPLETED,
                EAncillaryState.DISABLED,
              ].includes(componentDetail.stateUniqueId as EAncillaryState) &&
              componentDetail?.latitude
                ? navigateToMap
                : undefined
            }
            showOnMapButtonId="component-show-on-map"
            editPermissions={[EPermission.EDIT_ANCILLARY]}
            redirectToEditView={
              licenceAuthorityValid && componentDetail.customFields
                ? handleRedirectToEditPageButton()
                : undefined
            }
            redirectToDisableView={
              componentDetail.stateUniqueId === EAncillaryState.DISABLED ||
              !hasPermissions([EPermission.DISABLE_ANCILLARY]) ||
              !licenceAuthorityValid
                ? undefined
                : () =>
                    navigate(
                      `${ERouteLinks.DisableAncillary.replace(
                        ':id',
                        componentDetail.uniqueId,
                      ).replace(':type', EArtAncillaryTypeName.COMPONENTS.toLowerCase())}`,
                    )
            }
            redirectToMovedTransaction={handleRedirectToMovedTransaction}
            hideMovedTransaction={
              !licenceAuthorityValid ||
              statesWithoutMovedTransaction.includes(
                componentDetail?.stateUniqueId as EState,
              ) ||
              !hasPermissions(EPermission.ADD_TRANSACTION)
            }
            showEditButton={customFieldsAncillaries.length > 0}
          >
            <Grid
              container
              sx={{
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                justifyContent: 'center',
                gap: '20px',
              }}
            >
              <Grid container sx={{ gap: '12px', alignContent: 'flex-start', width: '270px' }}>
                <ImagesGalleryRadioButton
                  setGalleryType={setGalleryType}
                  galleryType={galleryType}
                  moduleType={EModuleType.ANCILLARY}
                />
                <ImagesGallery
                  key={'ancillary_details_gallery'}
                  moduleType={EModuleType.ANCILLARY}
                  ownerId={componentDetail.uniqueId}
                  init={() => setGalleryType(EGalleryTypeAncillary.Owner)}
                  buttonId={'ancillary_details-add_photo'}
                  buttonPermission={EPermission.EDIT_ANCILLARY}
                  hidden={galleryType !== EGalleryTypeAncillary.Owner}
                  onClickEdit={() =>
                    navigate(
                      `${ERouteLinks.ComponentsEditImages.replace(
                        ':id',
                        componentDetail.uniqueId as string,
                      )}`,
                    )
                  }
                  onClickAdd={() =>
                    navigate(
                      `${ERouteLinks.ComponentsAddImages.replace(
                        ':id',
                        componentDetail.uniqueId as string,
                      )}`,
                    )
                  }
                />
                <ImagesGallery
                  key={'ancillary_details_ART_gallery'}
                  moduleType={EModuleType.ART}
                  ownerId={componentDetail.artUniqueId}
                  buttonId={'ancillary_details_ART-add_photo'}
                  buttonPermission={EPermission.MANAGE_LOCAL_ART}
                  hidden={galleryType !== EGalleryTypeAncillary.ART}
                  showButton={false}
                />
              </Grid>
              <Grid
                sx={{
                  width: { xs: '100%', md: 'calc(100% - 270px)' },
                  rowGap: 1,
                  alignContent: 'flex-start',
                }}
              >
                <ComponentInformationContainer size={{ xs: 12 }} container>
                  <Grid size={{ xs: 12 }}>
                    <Stack>
                      <ComponentInformationTitle variant="subtitle1">
                        {t(EComponentLabels.componentGeneralInformation)}
                      </ComponentInformationTitle>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.componentType)}
                      </Typography>
                      <Typography variant="body2">
                        {findInDictionary(componentDetail?.typeUniqueId, artComponentType)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography
                        variant="subtitle2"
                        sx={{ marginBottom: '4px', textTransform: 'uppercase' }}
                      >
                        {t(EComponentLabels.artId)}
                      </Typography>
                      <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                        {componentDetail?.artId}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, lg: 6 }}>
                    <Stack>
                      {componentDetail.artUniqueId && (
                        <Link
                          href={ERouteLinks.ViewAncillaryDetails.replace(
                            ':id',
                            componentDetail.artUniqueId,
                          ).replace(':type', EArtAncillaryTypeNameLowerCase.COMPONENTS)}
                          underline="none"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            variant={EButtonVariants.contained}
                            size={EButtonSizes.small}
                            label={t('componentDetails.showArtInformation.button')}
                            fullWidth
                          />
                        </Link>
                      )}
                    </Stack>
                  </Grid>

                  {showField('manufacturer') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.manufacturer)}
                        </Typography>
                        <Typography variant="body2">
                          {findInDictionary(
                            componentDetail.manufacturerUniqueId,
                            artComponentManufacturer,
                          )}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('productName') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.productName)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(componentDetail?.productName)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('calibre') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.calibre)}
                        </Typography>
                        <Typography variant="body2">
                          {findInDictionary(
                            componentDetail?.calibreUniqueId,
                            artComponentCalibre,
                          )}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('composition') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.composition)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(componentDetail?.composition)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('proofHouse') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.proofHouse)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(componentDetail?.proofHouse)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('capacity') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.capacity)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(componentDetail?.capacity)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  {showField('length') && (
                    <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                      <Stack>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t(EComponentLabels.length)}
                        </Typography>
                        <Typography variant="body2">
                          {emptyFieldHolder(componentDetail?.length)}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                </ComponentInformationContainer>
                <ComponentInformationContainer size={{ xs: 12 }} container>
                  <Grid size={{ xs: 12 }}>
                    <Stack>
                      <ComponentInformationTitle variant="subtitle1">
                        {t(EComponentLabels.componentDetailedInformation)}
                      </ComponentInformationTitle>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.firecycleID)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(componentDetail?.fireCycleId)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.serialNumber)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(componentDetail?.serialNumber)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.componentState)}
                      </Typography>
                      <Typography variant="body2">
                        <Chips
                          text={emptyFieldHolder(componentDetail?.state)}
                          variant={
                            renderDataColorByValues(
                              componentDetail.activityUniqueId,
                              componentDetail.legalityUniqueId,
                              componentDetail.stateUniqueId,
                            ).stateField
                          }
                        />
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.exhibitNumber)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(componentDetail?.exhibitNumber)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.currentOwner)}
                      </Typography>
                      <Typography variant="body2">
                        <Typography variant="body2">
                          <StyledLegalLink
                            legalId={componentDetail.ownerId}
                            legalType={getLegalEntityType(componentDetail.ownerType)}
                            labelText={componentDetail.ownerName}
                            legalVisibilityUniqueId={componentDetail.ownerVisibilityUniqueId}
                          />
                        </Typography>
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.currentKeeper)}
                      </Typography>
                      <Typography variant="body2">
                        <Typography variant="body2">
                          <StyledLegalLink
                            legalId={componentDetail.keeperId}
                            legalType={getLegalEntityType(componentDetail.keeperType)}
                            labelText={componentDetail.keeperName}
                            legalVisibilityUniqueId={componentDetail.keeperVisibilityUniqueId}
                          />
                        </Typography>
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.manufacturedDate)}
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(componentDetail?.productionDate, shortDateFormat)}
                      </Typography>
                    </Stack>
                  </Grid>
                  {componentDetail?.customFields?.map((customField) => (
                    <Grid size={{ xs: 6, lg: 3 }} key={customField.customFieldUniqueId}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {customField?.translations?.filter(
                          (translation) =>
                            selectedLanguage?.uniqueId === translation.languageUniqueId,
                        )?.[0]?.name ||
                          customField?.translations?.filter(
                            (translation) =>
                              defaultLanguage?.uniqueId === translation.languageUniqueId,
                          )?.[0]?.name}
                      </Typography>
                      <Typography variant="body2">
                        {renderCustomFieldData(customField)}
                      </Typography>
                    </Grid>
                  ))}
                </ComponentInformationContainer>
                <ComponentInformationContainer
                  size={{ xs: 12 }}
                  container
                  borderHidden={!showMarkings}
                >
                  <Grid size={{ xs: 12 }}>
                    <Stack>
                      <ComponentInformationTitle variant="subtitle1">
                        {t(EComponentLabels.firearmInformation)}
                      </ComponentInformationTitle>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.firearmManufacturer)}
                      </Typography>
                      <Typography variant="body2">
                        {findInDictionary(
                          componentDetail?.frtManufacturerUniqueId,
                          manufacturer,
                        )}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.firearmReferenceNo)}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(componentDetail?.frtReferenceNo)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, lg: 6 }}>
                    <Stack>
                      {hasPermissions([EPermission.VIEW_FIREARMS]) && (
                        <Link
                          href={ERouteLinks.FRTDetails.replace(
                            ':frtId',
                            componentDetail.frtId,
                          )}
                          underline="none"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            variant={EButtonVariants.contained}
                            size={EButtonSizes.small}
                            label={t('componentDetails.showFrtInformation.button')}
                            fullWidth
                          />
                        </Link>
                      )}
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.firearmFirecycleType)}
                      </Typography>
                      <Typography variant="body2">
                        {findInDictionary(componentDetail?.frtTypeUniqueId, firearmType)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.firearmMake)}
                      </Typography>
                      <Typography variant="body2">
                        {findInDictionary(componentDetail?.frtMakeUniqueId, make)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(EComponentLabels.firearmModel)}
                      </Typography>
                      <Typography variant="body2">
                        {findInDictionary(componentDetail?.frtModelUniqueId, model)}
                      </Typography>
                    </Stack>
                  </Grid>
                </ComponentInformationContainer>
                <ComponentInformationContainer size={{ xs: 12 }} container borderHidden>
                  {componentDetail.markings && showMarkings && (
                    <>
                      <Grid size={{ xs: 12 }}>
                        <Stack>
                          <ComponentInformationTitle variant="subtitle1">
                            {t(EComponentLabels.componentMarkings)}
                          </ComponentInformationTitle>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.importMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              componentDetail.markings.find(
                                (marking) => marking.markingType === EMarkingTypes.IMPORT,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.appliedMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              componentDetail.markings.find(
                                (marking) => marking.markingType === EMarkingTypes.APPLIED,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.deactivationMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              componentDetail.markings.find(
                                (marking) =>
                                  marking.markingType === EMarkingTypes.DEACTIVATION,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.exportMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              componentDetail.markings.find(
                                (marking) => marking.markingType === EMarkingTypes.EXPORT,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.surplusSaleMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              componentDetail.markings.find(
                                (marking) =>
                                  marking.markingType === EMarkingTypes.SURPLUS_SALE,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.yearOfTransfer)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(componentDetail.yearOfTransfer)}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.confiscationMarking)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(
                              componentDetail.markings.find(
                                (marking) =>
                                  marking.markingType === EMarkingTypes.CONFISCATION,
                              )?.marking,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(EComponentLabels.yearOfConfiscation)}
                          </Typography>
                          <Typography variant="body2">
                            {emptyFieldHolder(componentDetail.yearOfConfiscation)}
                          </Typography>
                        </Stack>
                      </Grid>
                    </>
                  )}
                  {componentDetail.markings && (
                    <Grid
                      container
                      size={{ xs: 12 }}
                      sx={{ justifyContent: 'flex-end', mt: 3 }}
                    >
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Button
                          fullWidth
                          label={
                            showMarkings
                              ? t('ancillaryDetails.hideAncillaryMarkings.button')
                              : t('ancillaryDetails.seeAncillaryMarkings.button')
                          }
                          variant={EButtonVariants.outlined}
                          size={EButtonSizes.small}
                          onClick={() => setShowMarkings(!showMarkings)}
                        />
                      </Grid>
                    </Grid>
                  )}
                </ComponentInformationContainer>
              </Grid>
            </Grid>
          </DetailsTemplate>
          <Grid size={{ xs: 12 }}>
            <Stack sx={{ pb: 2, pt: 2 }}>
              {nextPageAlert && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
                </Box>
              )}
              {ancillaryAlert && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Alert text={ancillaryAlert.text} variant={ancillaryAlert.variant} />
                </Box>
              )}
              {transactionAlert && (
                <Box sx={{ marginBottom: '16px' }}>
                  <Alert text={transactionAlert.text} variant={transactionAlert.variant} />
                </Box>
              )}
              {hasPermissions([EPermission.VIEW_LIMITED_ANCILLARIES]) && (
                <Button
                  label={
                    isAllExpanded
                      ? t('componentDetails.collapseAll.button')
                      : t('componentDetails.expandAll.button')
                  }
                  onClick={() => setIsAllExpanded((prev) => !prev)}
                  variant={EButtonVariants.outlined}
                  sx={{ width: '150px', alignSelf: 'flex-end', mt: 2 }}
                />
              )}
            </Stack>
          </Grid>
        </Grid>
      )}
      {hasPermissions(
        [EPermission.VIEW_LIMITED_ANCILLARIES, EPermission.VIEW_ALL_ANCILLARIES],
        true,
      ) && (
        <Accordion
          title={t('componentDetails.transactions.header')}
          name="transactions"
          key="transactions"
          expanded={isAllExpanded}
        >
          <ComponentsTransactions
            inGroup={
              !!componentDetail?.groupName &&
              ![EFirearmStateId.EXPORTED].includes(
                componentDetail?.stateUniqueId as EFirearmStateId,
              )
            }
            ownerId={componentDetail?.ownerId}
            ownerVisibilityUniqueId={componentDetail?.ownerVisibilityUniqueId}
            keeperId={componentDetail?.keeperId}
            keeperVisibilityUniqueId={componentDetail?.keeperVisibilityUniqueId}
            stateUniqueId={componentDetail?.stateUniqueId}
            hideManagement={!licenceAuthorityValid}
          />
        </Accordion>
      )}
      {hasPermissions([EPermission.MANAGE_COMMENTS_ANCILLARY]) && (
        <Accordion
          title={t('componentDetails.comments.header')}
          name="comments"
          key="comments"
          expanded={isAllExpanded}
        >
          <ComponentsComments id={id} hideManagement={!licenceAuthorityValid} />
        </Accordion>
      )}
      {hasPermissions([EPermission.MANAGE_ATTACHMENT_ANCILLARY]) && (
        <Accordion
          title={t('componentDetails.attachments.header')}
          name="attachments"
          key="attachments"
          expanded={isAllExpanded}
        >
          <ComponentsAttachments idAncillary={id} hideManagement={!licenceAuthorityValid} />
        </Accordion>
      )}
    </div>
  );
};

export default DetailComponents;
